import { Region, State, VehicleCategory } from "@/gql/graphql";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { WaitlistForm } from "@/app/Waitlists/Forms/WaitlistForm";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { createWaitlistMutation } from "@/app/Waitlists/GraphQL/waitlistMutations";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";

export type WaitlistInput = {
  latest_departure_date: string;
  earliest_departure_date: string;
  region: Region;
  user_id: string;
  departure_location: string;
  delivery_location: string;
  vehicle_category: VehicleCategory | "";
  number_of_travellers: number | null;
};
export function WaitlistCreateDialog({
  initialValues,
}: {
  initialValues?: Partial<WaitlistInput>;
}) {
  const { mutateAsync } = useGqlMutation(createWaitlistMutation);
  const { close } = useDialogState();

  return (
    <BaseDialog title="Add waitlist">
      <GenericForm<WaitlistInput>
        initialValues={{
          latest_departure_date: null!,
          earliest_departure_date: null!,
          region: null!,
          user_id: null!,
          delivery_location: "",
          departure_location: "",
          vehicle_category: "",
          number_of_travellers: null,
          ...initialValues,
        }}
        onSubmit={async (values) => {
          //Parse the location to find out if it was a state or city
          const departureLocation = parseLocation(values.departure_location);
          const deliveryLocation = parseLocation(values.delivery_location);

          await mutateAsync({
            input: {
              earliest_departure_date: values.earliest_departure_date,
              latest_departure_date: values.latest_departure_date,
              user_id: values.user_id,
              vehicle_category: values.vehicle_category || null,
              number_of_travellers: values.number_of_travellers,
              ...(departureLocation.type === "city"
                ? {
                    departure_city_id: departureLocation.value,
                  }
                : {
                    departure_state: departureLocation.value,
                  }),
              ...(deliveryLocation.type === "city"
                ? {
                    delivery_city_id: deliveryLocation.value,
                  }
                : {
                    delivery_state: deliveryLocation.value,
                  }),
            },
          });

          close();
        }}
      >
        <WaitlistForm />

        <DialogSubmitButton text="Add waitlist" />
      </GenericForm>
    </BaseDialog>
  );
}

type LocationValue =
  | {
      type: "state";
      value: State;
    }
  | {
      type: "city";
      value: string;
    };

function parseLocation(value: string): LocationValue {
  if (value.includes("state:")) {
    return {
      type: "state",
      value: value.substring(6) as State,
    };
  }

  if (value.includes("city:")) {
    return {
      type: "city",
      value: value.substring(5),
    };
  }

  throw new Error(`Unexpected value for location: ${value}`);
}
